import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { truncate } from 'lodash';
import classNames from 'classnames';
import { Menu } from '@headlessui/react';
import { Plus, Settings } from 'lucide-react';
import Button from '../../components/Button';
import UserAvatar from '../../components/UserAvatar';
import CreateBmcModal from './CreateBmcModal';
import UpdateBmcModal from './UpdateBmcModal';

const colorClasses = [
  'bmc-team-users-user--orange',
  'bmc-team-users-user--green',
  'bmc-team-users-user--blue',
  'bmc-team-users-user--violet',
];

export default function Team({ locales, locales: { teams } }) {
  const [menuOpened, setMenuOpened] = useState(false);
  const [createBmcOpened, setCreateBmcOpened] = useState(false);
  const [updateBmcOpened, setUpdateBmcOpened] = useState(false);
  const history = useHistory();
  const { bmcId: bmcIdFromParams } = useParams();

  const {
    bmc: {
      current: { id, name },
      collection,
      users,
    },
  } = useSelector((state) => state);
  const {
    bmc: { fetchCurrentBmc, fetchBmcUsers },
  } = useDispatch();

  useEffect(() => {
    if (!id) fetchCurrentBmc(bmcIdFromParams);
  }, []);

  useEffect(() => {
    if (id) {
      fetchBmcUsers(id);
    }
  }, [id]);

  const visibleUsers = users.slice(0, 3);
  const nonVisibleUsers = users.slice(3);

  const settingsBmc = () => {
    setMenuOpened(false);
    setUpdateBmcOpened(true);
  };

  const projectName = (project) => {
    return project.name
      ? project.name[0] + (project.name[1] ? project.name[1] : '')
      : project.id;
  };

  const switchBmc = (projectId) => {
    const path = window.location.pathname;

    fetchCurrentBmc(projectId);
    fetchBmcUsers(projectId);

    if (path.startsWith('/bmcs')) {
      const newPath = path.replace(/(\/bmcs\/)\d+(\/?\w*)/, `$1${projectId}$2`);
      history.push(newPath);
      // scrollToTop();
    }
  };

  const calculateColorClassname = (project) => {
    return colorClasses[project.id % colorClasses.length];
  };

  return (
    <div className="bmc-team">
      <Menu>
        <Menu.Button
          onClick={() => setMenuOpened(!menuOpened)}
          className="bmc-team-title"
          // eslint-disable-next-line react/no-unknown-property
          test-key="open-projects-modal"
        >
          {truncate(name, { length: 25 })}
          {menuOpened ? (
            <i className="icon-Colaps" />
          ) : (
            <i className="icon-SideBar-right" />
          )}
        </Menu.Button>
        <Menu.Items className="bmc-team-menu p-3">
          {collection.map((bmc, index) => (
            <Menu.Item key={bmc.id + index}>
              {({ active }) => (
                <div className="flex flex-row justify-center align-center gap-2">
                  <Button
                    tabIndex={0}
                    variant="ghost"
                    key={bmc.id}
                    className={`p-2.5 bmc-team-menu-item align-start text-primary-text ${
                      active && 'bg-muted'
                    }`}
                    onClick={() => switchBmc(bmc.id)}
                  >
                    <div className="bmc-team-menu-item-icon">
                      <div
                        className={classNames(
                          'bmc-team-users-user',
                          calculateColorClassname(bmc),
                        )}
                      >
                        {projectName(bmc)}
                      </div>
                    </div>
                    <div className="text-start justify-start text-primary-text max-w-28 w-28 overflow-hidden">
                      {bmc.name}
                    </div>
                  </Button>
                </div>
              )}
            </Menu.Item>
          ))}

          <Menu.Item>
            {({ active }) => (
              <Button
                variant="outline"
                className={`mt-4 ${active && 'border-primary'}`}
                onClick={() => {
                  setCreateBmcOpened(true);
                  // setMenuOpened(false);
                }}
              >
                <Plus />
                {teams.createNewProject}
              </Button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Menu>

      <Button
        aria-label="Settings"
        tabIndex={0}
        className="ml-3"
        variant="ghost"
        onClick={() => settingsBmc(id)}
      >
        <Settings
          className="w-5 min-w-5 h-5 min-h-5 text-gray-300"
          test-key="project-settings-button"
        />
      </Button>

      <Button
        variant="ghost"
        size="wrapped"
        className="ml-16"
        onClick={() => {
          setUpdateBmcOpened(true);
        }}
      >
        {visibleUsers.map((user) => (
          <UserAvatar
            key={user.id}
            user={user}
            className="user-avatar--orange"
          />
        ))}
        {nonVisibleUsers.length > 0 && (
          <div
            role="button"
            tabIndex="0"
            className="user-avatar user-avatar--blue"
          >
            +{nonVisibleUsers.length}
          </div>
        )}
      </Button>

      {createBmcOpened && (
        <CreateBmcModal
          visible={createBmcOpened}
          locales={locales}
          onClose={() => setCreateBmcOpened(false)}
        />
      )}

      {updateBmcOpened && (
        <UpdateBmcModal
          visible={updateBmcOpened}
          locales={locales}
          bmcId={id}
          onClose={() => setUpdateBmcOpened(false)}
        />
      )}
    </div>
  );
}
